import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Grid,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Stack,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  AutoAwesome as AutoAwesomeIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import axios from '../../api/axios';
import MarkdownEditor from '../Common/MarkdownEditor';

const AddExerciseDialog = ({ open, onClose, notebookId, sectionTitle, onSuccess }) => {
  const [activeTab, setActiveTab] = useState(0); // 0: 手动输入, 1: AI生成
  const [questionType, setQuestionType] = useState('multiple_choice');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [explanation, setExplanation] = useState('');
  const [proof, setProof] = useState('');
  const [options, setOptions] = useState(['', '', '', '']);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [blanks, setBlanks] = useState([]);
  const [codeAnswer, setCodeAnswer] = useState('');
  const [publicTests, setPublicTests] = useState('');
  const [secretTests, setSecretTests] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // AI生成相关状态
  const [aiInputText, setAiInputText] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiUploadedFile, setAiUploadedFile] = useState(null);
  const [aiQuestionType, setAiQuestionType] = useState('multiple_choice');
  const [enableAI, setEnableAI] = useState(true); // 默认启用AI

  const questionTypes = [
    { value: 'multiple_choice', label: '选择题' },
    { value: 'code', label: '代码题（编程练习）' },
    { value: 'code_tracing', label: '代码追踪题' },
    { value: 'proof', label: '证明题' },
    { value: 'short_answer', label: '简答题' },
  ];

  const handleClose = () => {
    if (!saving && !aiGenerating) {
      // Reset form
      setActiveTab(0);
      setQuestionType('multiple_choice');
      setQuestion('');
      setAnswer('');
      setExplanation('');
      setProof('');
      setOptions(['', '', '', '']);
      setCorrectAnswer('');
      setBlanks([]);
      setCodeAnswer('');
      setPublicTests('');
      setSecretTests('');
      setUploadedImages([]);
      setError('');
      setAiInputText('');
      setAiUploadedFile(null);
      setAiQuestionType('multiple_choice');
      onClose();
    }
  };

  // 处理文件上传（AI生成）
  const handleAiFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setAiUploadedFile(file);
    setError('');

    // 读取文件内容
    const reader = new FileReader();
    reader.onload = async (e) => {
      const fileContent = e.target.result;
      setAiInputText(fileContent);
    };
    reader.readAsText(file);
  };

  // AI生成题目
  const handleAiGenerate = async () => {
    if (!aiInputText.trim() && !aiUploadedFile) {
      setError('请输入问题文本或上传文件');
      return;
    }

    setAiGenerating(true);
    setError('');

    try {
      // 获取章节信息（用于上下文）
      let sectionInfo = {};
      if (notebookId && sectionTitle) {
        try {
          const notebookRes = await axios.get(`/notes/${notebookId}`);
          if (notebookRes.data && notebookRes.data.sections) {
            const section = notebookRes.data.sections[sectionTitle];
            if (section) {
              sectionInfo = {
                section_title: sectionTitle,
                section_description: section.introduction || ''
              };
            }
          }
        } catch (e) {
          console.warn('Failed to fetch section info:', e);
        }
      }

      // 为 AI 生成请求使用更长的超时时间（5分钟）
      const response = await axios.post('/notebook-examples/generate-from-text', {
        user_text: aiInputText.trim(),
        section_title: sectionInfo.section_title || sectionTitle || '',
        section_description: sectionInfo.section_description || '',
        question_type: aiQuestionType
      }, {
        timeout: 300000 // 5分钟 = 300000毫秒
      });

      if (response.data.success && response.data.question) {
        const generated = response.data.question;
        
        // 填充表单
        setQuestionType(generated.questionType || 'multiple_choice');
        setQuestion(generated.question || '');
        setAnswer(generated.answer || '');
        setExplanation(generated.explanation || '');
        setProof(generated.proof || '');
        
        // 处理选项数组
        if (generated.options && Array.isArray(generated.options)) {
          // 确保有4个选项，不足的用空字符串填充
          const newOptions = [...generated.options];
          while (newOptions.length < 4) {
            newOptions.push('');
          }
          setOptions(newOptions.slice(0, 4));
        } else if (generated.questionType === 'multiple_choice') {
          // 如果是选择题但没有选项，保持默认的4个空选项
          setOptions(['', '', '', '']);
        }
        
        setCorrectAnswer(generated.correctAnswer || '');
        setCodeAnswer(generated.codeAnswer || '');
        setPublicTests(generated.publicTests || '');
        setSecretTests(generated.secretTests || '');

        // 切换到手动输入tab，让用户查看和编辑
        setActiveTab(0);
        setAiInputText('');
        setAiUploadedFile(null);
        
        // 显示成功提示
        setError('');
      } else {
        setError('生成失败：' + (response.data.error || '未知错误'));
      }
    } catch (err) {
      console.error('Error generating question:', err);
      setError('生成失败：' + (err.response?.data?.error || err.message));
    } finally {
      setAiGenerating(false);
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setError('');

    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);

        const response = await axios.post('/upload/image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        return {
          url: response.data.url,
          filename: file.name,
        };
      });

      const uploaded = await Promise.all(uploadPromises);
      setUploadedImages((prev) => [...prev, ...uploaded]);
    } catch (err) {
      console.error('Error uploading images:', err);
      setError('图片上传失败：' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const insertImageToQuestion = (imageUrl) => {
    const imageMarkdown = `![图片](${imageUrl})\n\n`;
    setQuestion((prev) => prev + imageMarkdown);
  };

  const handleAddOption = () => {
    setOptions((prev) => [...prev, '']);
  };

  const handleRemoveOption = (index) => {
    if (options.length > 2) {
      setOptions((prev) => prev.filter((_, i) => i !== index));
      if (correctAnswer && correctAnswer.charCodeAt(0) - 'A'.charCodeAt(0) >= options.length - 1) {
        setCorrectAnswer('');
      }
    }
  };

  const handleOptionChange = (index, value) => {
    setOptions((prev) => {
      const newOptions = [...prev];
      newOptions[index] = value;
      return newOptions;
    });
  };

  // Reserved for future fill-in-the-blank exercise support
  // const handleAddBlank = () => {
  //   setBlanks((prev) => [...prev, { text: '', answer: '' }]);
  // };

  // const handleRemoveBlank = (index) => {
  //   setBlanks((prev) => prev.filter((_, i) => i !== index));
  // };

  // const handleBlankChange = (index, field, value) => {
  //   setBlanks((prev) => {
  //     const newBlanks = [...prev];
  //     newBlanks[index] = { ...newBlanks[index], [field]: value };
  //     return newBlanks;
  //   });
  // };

  const handleSubmit = async () => {
    setError('');

    // Validation
    if (!question.trim()) {
      setError('请输入题目内容');
      return;
    }

    if (questionType === 'multiple_choice') {
      const validOptions = options.filter((opt) => opt.trim());
      if (validOptions.length < 2) {
        setError('至少需要2个选项');
        return;
      }
      if (!correctAnswer) {
        setError('请选择正确答案');
        return;
      }
    }

    if (questionType === 'code') {
      if (!codeAnswer.trim()) {
        setError('请输入代码答案');
        return;
      }
    }

    if (questionType === 'proof' && !proof.trim()) {
      setError('请输入证明内容');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        notebookId,
        sectionTitle,
        questionType,
        question: question.trim(),
        answer: answer.trim() || null,
        explanation: explanation.trim() || null,
        proof: proof.trim() || null,
        options: questionType === 'multiple_choice' ? options.filter((opt) => opt.trim()) : null,
        correctAnswer: correctAnswer || null,
        blanks: blanks.length > 0 ? blanks : null,
        codeAnswer: codeAnswer.trim() || null,
        publicTests: publicTests.trim() || null,
        secretTests: secretTests.trim() || null,
        enableAI: enableAI,
      };

      await axios.post('/notebook-examples', payload);

      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (err) {
      console.error('Error creating exercise:', err);
      setError('创建失败：' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">添加题目</Typography>
          <IconButton onClick={handleClose} disabled={saving} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab 
              icon={<EditIcon />} 
              iconPosition="start"
              label="手动输入" 
              disabled={saving || aiGenerating}
            />
            <Tab 
              icon={<AutoAwesomeIcon />} 
              iconPosition="start"
              label="AI生成" 
              disabled={saving || aiGenerating}
            />
          </Tabs>
        </Box>

        {/* AI生成 Tab */}
        {activeTab === 1 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              输入问题文本，AI将自动生成结构化题目
            </Typography>
            
            <Stack spacing={2}>
              {/* 题型选择 */}
              <FormControl fullWidth>
                <InputLabel>题目类型</InputLabel>
                <Select
                  value={aiQuestionType}
                  onChange={(e) => setAiQuestionType(e.target.value)}
                  label="题目类型"
                  disabled={aiGenerating || saving}
                >
                  {questionTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 文件上传 */}
              <Box>
                <input
                  accept=".txt,.md,.markdown"
                  style={{ display: 'none' }}
                  id="ai-file-upload"
                  type="file"
                  onChange={handleAiFileUpload}
                  disabled={aiGenerating || saving}
                />
                <label htmlFor="ai-file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={aiGenerating || saving}
                    fullWidth
                  >
                    上传问题文件（.txt, .md）
                  </Button>
                </label>
                {aiUploadedFile && (
                  <Chip
                    label={aiUploadedFile.name}
                    onDelete={() => {
                      setAiUploadedFile(null);
                      setAiInputText('');
                    }}
                    sx={{ mt: 1 }}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>

              {/* 文本输入 */}
              <TextField
                label="问题文本"
                fullWidth
                multiline
                rows={8}
                value={aiInputText}
                onChange={(e) => setAiInputText(e.target.value)}
                placeholder="输入问题文本，例如：&#10;1. 什么是栈？请选择正确答案：&#10;A. 先进先出的数据结构&#10;B. 后进先出的数据结构&#10;C. 随机访问的数据结构&#10;D. 无序的数据结构&#10;&#10;或者：&#10;编写一个函数计算斐波那契数列的第n项..."
                disabled={aiGenerating || saving}
                sx={{ mb: 2 }}
              />

              {/* 生成按钮 */}
              <Button
                variant="contained"
                onClick={handleAiGenerate}
                disabled={aiGenerating || saving || !aiInputText.trim()}
                startIcon={aiGenerating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                fullWidth
                size="large"
              >
                {aiGenerating ? 'AI生成中...' : '生成题目'}
              </Button>

              {aiGenerating && (
                <Alert severity="info">
                  AI正在分析您的问题并生成结构化题目，请稍候...
                </Alert>
              )}
            </Stack>
          </Box>
        )}

        {/* 手动输入 Tab */}
        {activeTab === 0 && (
        <Grid container spacing={2}>
          {/* Question Type */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>题目类型</InputLabel>
              <Select
                value={questionType}
                onChange={(e) => setQuestionType(e.target.value)}
                label="题目类型"
                disabled={saving}
              >
                {questionTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Image Upload */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload"
                type="file"
                multiple
                onChange={handleImageUpload}
                disabled={uploading || saving}
              />
              <label htmlFor="image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading || saving}
                  sx={{ mr: 1 }}
                >
                  {uploading ? '上传中...' : '上传图片'}
                </Button>
              </label>
              {uploadedImages.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {uploadedImages.map((img, index) => (
                    <Chip
                      key={index}
                      label={img.filename}
                      onDelete={() => handleRemoveImage(index)}
                      onClick={() => insertImageToQuestion(img.url)}
                      color="primary"
                      variant="outlined"
                      disabled={saving}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Question */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              题目内容 *
            </Typography>
            <MarkdownEditor
              value={question}
              onChange={setQuestion}
              label="题目内容"
              minRows={4}
              maxRows={10}
              showPreview={true}
              showToolbar={true}
              placeholder="输入题目内容（支持Markdown格式和图片）..."
              disabled={saving}
            />
          </Grid>

          {/* Multiple Choice Options */}
          {questionType === 'multiple_choice' && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  选项 *
                </Typography>
                {options.map((option, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && e.stopPropagation()}
                      placeholder={`选项 ${String.fromCharCode(65 + index)}（可回车换行）`}
                      disabled={saving}
                      inputProps={{ style: { whiteSpace: 'pre-wrap' } }}
                    />
                    {options.length > 2 && (
                      <IconButton
                        onClick={() => handleRemoveOption(index)}
                        disabled={saving}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddOption}
                  disabled={saving || options.length >= 10}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  添加选项
                </Button>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>正确答案 *</InputLabel>
                  <Select
                    value={correctAnswer}
                    onChange={(e) => setCorrectAnswer(e.target.value)}
                    label="正确答案 *"
                    disabled={saving}
                  >
                    {options.map((_, index) => (
                      <MenuItem key={index} value={String.fromCharCode(65 + index)}>
                        {String.fromCharCode(65 + index)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {/* Code Answer */}
          {questionType === 'code' && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  代码答案 *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={codeAnswer}
                  onChange={(e) => setCodeAnswer(e.target.value)}
                  placeholder="输入代码答案..."
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  公开测试用例
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={publicTests}
                  onChange={(e) => setPublicTests(e.target.value)}
                  placeholder="输入公开测试用例（JSON格式）..."
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  隐藏测试用例
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={secretTests}
                  onChange={(e) => setSecretTests(e.target.value)}
                  placeholder="输入隐藏测试用例（JSON格式）..."
                  disabled={saving}
                />
              </Grid>
            </>
          )}

          {/* Proof */}
          {questionType === 'proof' && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                证明内容 *
              </Typography>
              <MarkdownEditor
                value={proof}
                onChange={setProof}
                label="证明内容"
                minRows={6}
                maxRows={15}
                showPreview={true}
                showToolbar={true}
                placeholder="输入证明内容（支持Markdown格式）..."
                disabled={saving}
              />
            </Grid>
          )}

          {/* Short Answer */}
          {questionType === 'short_answer' && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                参考答案
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="输入参考答案..."
                disabled={saving}
              />
            </Grid>
          )}

          {/* Code Tracing */}
          {questionType === 'code_tracing' && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  选项（可选，如果不提供则为简答题模式）
                </Typography>
                {options.map((option, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && e.stopPropagation()}
                      placeholder={`选项 ${String.fromCharCode(65 + index)}（可回车换行）`}
                      disabled={saving}
                      inputProps={{ style: { whiteSpace: 'pre-wrap' } }}
                    />
                    {options.length > 2 && (
                      <IconButton
                        onClick={() => handleRemoveOption(index)}
                        disabled={saving}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddOption}
                  disabled={saving || options.length >= 10}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  添加选项
                </Button>
              </Grid>
              {options.filter((opt) => opt.trim()).length > 0 && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>正确答案</InputLabel>
                    <Select
                      value={correctAnswer}
                      onChange={(e) => setCorrectAnswer(e.target.value)}
                      label="正确答案"
                      disabled={saving}
                    >
                      {options.map((_, index) => (
                        <MenuItem key={index} value={String.fromCharCode(65 + index)}>
                          {String.fromCharCode(65 + index)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </>
          )}

          {/* Explanation */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              解析（可选）
            </Typography>
            <MarkdownEditor
              value={explanation}
              onChange={setExplanation}
              label="解析"
              minRows={3}
              maxRows={8}
              showPreview={true}
              showToolbar={true}
              placeholder="输入题目解析（支持Markdown格式）..."
              disabled={saving}
            />
          </Grid>

          {/* Enable AI Switch */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={enableAI}
                  onChange={(e) => setEnableAI(e.target.checked)}
                  disabled={saving}
                />
              }
              label="启用AI助手（启用后可以使用AI助手、AI评分，对话会加入memory；禁用后提交即通过，直接显示答案，适合包含图片的题目）"
            />
          </Grid>
        </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          取消
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || uploading}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? '创建中...' : '创建题目'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddExerciseDialog;

